import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
// GAP: no DS icon for taking / uploading a photo (Camera); lucide in both modes (design sheet §3.1, logged not substituted).
import { Camera, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useValidatePhoneNumber } from '@/patients/services/use-validate-phone-number';
import { useSaveEmergencyContacts } from '@/patients/services/use-save-emergency-contacts';
import { useRelationships, type Relationship } from '@/services/common/use-relationships';
import type { AppointmentFormValues } from '@/types/appointment-form';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * Country dial-code list — Flutter parity for the picker (screenshot
 * shows GCC + IN + UM/CA/US + a long Caribbean tail). Renders flag from
 * flagcdn.com (free CDN, no auth, no npm dep) keyed by ISO2 lowercase.
 * Windows doesn't render emoji flags so we use real images here.
 */
interface CountryEntry {
  iso2: string;
  dial: string;
  name: string;
}
const COUNTRIES: CountryEntry[] = [
  { iso2: 'ae', dial: '+971', name: 'United Arab Emirates' },
  { iso2: 'eg', dial: '+20', name: 'Egypt' },
  { iso2: 'qa', dial: '+974', name: 'Qatar' },
  { iso2: 'sa', dial: '+966', name: 'Saudi Arabia' },
  { iso2: 'om', dial: '+968', name: 'Oman' },
  { iso2: 'kw', dial: '+965', name: 'Kuwait' },
  { iso2: 'bh', dial: '+973', name: 'Bahrain' },
  { iso2: 'in', dial: '+91', name: 'India' },
  { iso2: 'ca', dial: '+1', name: 'Canada' },
  { iso2: 'us', dial: '+1', name: 'United States' },
  { iso2: 'gb', dial: '+44', name: 'United Kingdom' },
  { iso2: 'pk', dial: '+92', name: 'Pakistan' },
  { iso2: 'ph', dial: '+63', name: 'Philippines' },
  { iso2: 'ng', dial: '+234', name: 'Nigeria' },
  { iso2: 'jo', dial: '+962', name: 'Jordan' },
  { iso2: 'lb', dial: '+961', name: 'Lebanon' },
];

function flagSrc(iso2: string): string {
  return `https://flagcdn.com/24x18/${iso2.toLowerCase()}.png`;
}


interface CreateContactViewProps {
  /** Closes the view; commit=true after a successful save. */
  onClose: (commit?: boolean) => void;
}

/**
 * CreateContactView — full panel-content replacement for adding a new
 * emergency contact to the patient's community. Mirrors Flutter
 * `editCallBackContactProfile` step (calendar_screen.dart:1110).
 *
 * Fields & validation:
 *   - RELATIONSHIP * (Father / Mother / Spouse / Friend / …)
 *   - FIRST NAME *
 *   - MIDDLE NAME (optional)
 *   - LAST NAME *
 *   - CONTACT NUMBER * (+<country> prefix + national number)
 *   - ADDRESS (optional)
 *   - APARTMENT (optional)
 *
 * Save adds the contact to the form's `emergencyContacts` list and
 * auto-selects it as the callback contact. Backend persistence (POST
 * to a contact-create endpoint) is a follow-up — Flutter persists via
 * a separate save step too, so this matches local-add-then-confirm.
 */
export function CreateContactView({ onClose }: CreateContactViewProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const contacts = watch('emergencyContacts');
  const patientId = watch('patientId');

  // Picked relationship is the full Relationship master entry (id/name)
  // so the save payload can ship `relationship: <id>` + `relation` as
  // a fully-populated object (matches the existing rows the backend
  // returned). String-only "Father" trips a backend NRE because the
  // UUID lookup fails.
  const [relationship, setRelationship] = useState<Relationship | null>(null);
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryCode, setCountryCode] = useState('+971');
  const [countryIso2, setCountryIso2] = useState('ae');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [apartment, setApartment] = useState('');
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  // Phone validation — Flutter parity
  // (`field_and_label.dart:430-470` PhoneField branch). Logic:
  //   - empty phone → no error, no API call
  //   - phone digits < 7 → immediately set "Please, provide a valid
  //     number" and SKIP the API (Flutter line 464)
  //   - phone digits >= 7 → debounce 500ms, then the server-side carrier
  //     check (`useValidatePhoneNumber` → POST /api/Master/ValidatePhoneNumber;
  //     format-only verdict when no carrier verdict is available)
  //
  // Note: the lookup takes pure E.164 (`+971656756756`). The dash-separated
  // form Flutter uses elsewhere is for the *backend save*, not the lookup.
  const phoneDigits = phone.replace(/\D+/g, '');
  const e164 = `${countryCode}${phoneDigits}`;
  const debouncedLookup = useDebounce(e164, 500);
  const validatePhone = useValidatePhoneNumber();
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    if (phoneDigits.length === 0) {
      setPhoneError(null);
      return;
    }
    if (phoneDigits.length < 7) {
      setPhoneError('Please, provide a valid number');
      return;
    }
    setPhoneError(null);
    validatePhone.mutate(
      { contactNumber: debouncedLookup },
      {
        onSuccess: (result) => {
          // Flutter wording — field_and_label.dart:465.
          setPhoneError(result.valid ? null : 'Please, provide a valid number');
        },
        onError: () => {
          // Client/auth error from the check (an outage already resolves
          // to a format-only verdict) — surface a softer message so the
          // user can retry.
          setPhoneError('Could not validate phone number');
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedLookup]);

  const saveContacts = useSaveEmergencyContacts();

  const saveEnabled =
    !!patientId &&
    !!relationship?.id &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    phone.trim().length > 0 &&
    !phoneError &&
    !validatePhone.isPending &&
    !saveContacts.isPending;

  const onSave = () => {
    if (!saveEnabled || !patientId) return;
    const fullName = [firstName, middleName, lastName]
      .map((p) => p.trim())
      .filter(Boolean)
      .join(' ');
    // Flutter sends `+CountryCode-Digits` for the contactNumber
    // (verifyPhoneUseCase line 481). Mirror it here so the saved value
    // matches what Flutter writes.
    const contactNumber = `${countryCode}-${phoneDigits}`;
    if (!relationship?.id) return;
    const newRow: AppointmentFormValues['emergencyContacts'][number] = {
      // Local id used for selection until the backend returns the real
      // emergencyContactId. The save hook strips this before POST.
      id: `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      name: fullName,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      // Send the master-list UUID for `relationship` and the full
      // entry as `relation` so the backend's value-set lookup
      // resolves cleanly. Existing rows in the response use the same
      // shape (relationship == relation.id == relation.name == UUID).
      relationship: relationship.id,
      relation: {
        id: relationship.id,
        name: relationship.name,
        code: relationship.code,
        system: relationship.system,
      },
      phone: contactNumber,
    };
    const nextList = [...contacts, newRow];
    saveContacts.mutate(
      // The save endpoint takes the full list — append the new row to
      // the existing emergency-contacts the form is already holding
      // (auto-populated from PreBookSelectedPatient when the patient
      // was picked). Address / apartment for the new row come from the
      // form inputs; existing rows keep whatever shape they arrived in.
      {
        patientId,
        contacts: nextList.map((c) => {
          const isNewRow = c.id === newRow.id;
          return {
            ...c,
            firstName: c.firstName,
            lastName: c.lastName,
            contactNumber: c.phone,
            ...(isNewRow
              ? {
                  address: address.trim() || null,
                  apartment: apartment.trim() || null,
                }
              : {}),
          };
        }),
      },
      {
        onSuccess: (saved) => {
          // Backend returns the canonical list (with real ids). Match
          // the brand-new row by full name + phone since our local id
          // doesn't survive the round-trip, then select it as the
          // callback contact.
          const reloaded =
            (saved && saved.length > 0
              ? saved.map((c) => {
                  // Backend ships `relation` PascalCase; normalise so the
                  // form (and the save hook on next round-trip) sees the
                  // same lowercase shape patient-section produces.
                  const rawRel = (c.relation ?? {}) as {
                    Id?: string; Name?: string; Code?: string; System?: string;
                    id?: string; name?: string; code?: string; system?: string;
                  };
                  const relName = rawRel.Name ?? rawRel.name;
                  const relId = rawRel.Id ?? rawRel.id;
                  const cAny = c as typeof c & {
                    Building?: string; Apartment?: string;
                    Country?: string; City?: string; Address?: string;
                    Latitude?: string; Longitude?: string;
                    SubmittedDate?: string;
                  };
                  return {
                    id: c.emergencyContactId ?? c.id,
                    emergencyContactId: c.emergencyContactId,
                    name:
                      c.name ||
                      [c.firstName, c.middleName, c.lastName]
                        .filter(Boolean)
                        .join(' '),
                    firstName: c.firstName,
                    lastName: c.lastName,
                    middleName: c.middleName,
                    relationship: c.relationship ?? c.relationshipName,
                    relation: relId || relName
                      ? {
                          id: relId,
                          name: relName,
                          code: rawRel.Code ?? rawRel.code,
                          system: rawRel.System ?? rawRel.system,
                        }
                      : undefined,
                    phone: c.contactNumber ?? c.phone,
                    contactNumber: c.contactNumber,
                    photo: c.photoUrl ?? c.photo,
                    photoUrl: c.photoUrl,
                    isSelf: c.isSelf ?? (relName ?? '').toLowerCase() === 'self',
                    submittedDate: c.submittedDate ?? cAny.SubmittedDate ?? null,
                    building: c.building ?? cAny.Building ?? null,
                    apartment: c.apartment ?? cAny.Apartment ?? null,
                    country: c.country ?? cAny.Country ?? null,
                    city: c.city ?? cAny.City ?? null,
                    address: c.address ?? cAny.Address ?? null,
                    latitude: c.latitude ?? cAny.Latitude ?? null,
                    longitude: c.longitude ?? cAny.Longitude ?? null,
                  };
                })
              : nextList) as AppointmentFormValues['emergencyContacts'];
          setValue('emergencyContacts', reloaded);
          const justAdded =
            reloaded.find(
              (c) =>
                (c.firstName ?? '').trim() === firstName.trim() &&
                (c.lastName ?? '').trim() === lastName.trim() &&
                (c.phone ?? '').replace(/\D+/g, '') === phoneDigits,
            ) ?? reloaded[reloaded.length - 1];
          if (justAdded?.id) {
            setValue('callbackContactId', justAdded.id, { shouldValidate: true });
          }
          onClose(true);
        },
      },
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="booking-subhead" data-slot="booking-subhead">
        <button
          onClick={() => onClose(false)}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Back
        </button>
        <h2 className="booking-subhead-title">Create Contact</h2>
        <button
          type="button"
          onClick={onSave}
          disabled={!saveEnabled}
          className={cn(
            'text-sm font-medium',
            saveEnabled
              ? 'text-brand-primary hover:text-interactive-hover'
              : 'text-text-hint cursor-not-allowed',
          )}
        >
          Save
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-4 py-4">
          <div className="rounded-lg border border-border-default bg-surface-card p-4 space-y-4">
            {/* Photo placeholder — Flutter shows a camera icon as the
                upload affordance. Tap-to-upload is TODO. */}
            <div className="flex justify-center">
              <button
                type="button"
                className="h-20 w-20 rounded-full bg-grey-100 flex items-center justify-center hover:bg-grey-200"
                aria-label="Add photo (TODO)"
                title="Add photo (TODO)"
              >
                <Camera className="size-6 text-text-secondary" />
              </button>
            </div>

            <Field label="Relationship" required>
              <RelationshipInput
                selected={relationship}
                onChange={setRelationship}
              />
            </Field>

            <Field label="First Name" required>
              <TextInput
                value={firstName}
                onChange={setFirstName}
                placeholder="Enter first name"
              />
            </Field>

            <Field label="Middle Name">
              <TextInput
                value={middleName}
                onChange={setMiddleName}
                placeholder="Enter middle name"
              />
            </Field>

            <Field label="Last Name" required>
              <TextInput
                value={lastName}
                onChange={setLastName}
                placeholder="Enter last name"
              />
            </Field>

            <Field label="Contact Number" required>
              <div className="flex items-stretch gap-2 relative">
                <button
                  type="button"
                  onClick={() => {
                    setCountryPickerOpen((o) => !o);
                    setCountrySearch('');
                  }}
                  className="flex items-center gap-2 h-10 px-2 rounded-md bg-grey-100 hover:bg-grey-200"
                >
                  <img
                    src={flagSrc(countryIso2)}
                    alt={countryIso2.toUpperCase()}
                    width={24}
                    height={18}
                    className="rounded-sm flex-shrink-0"
                    loading="lazy"
                  />
                  <span className="text-sm text-text-primary tabular-nums">
                    {countryCode}
                  </span>
                  <ChevronDown className="size-4 text-text-secondary" />
                </button>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) =>
                    setPhone(e.target.value.replace(/[^\d\s]/g, '').slice(0, 20))
                  }
                  placeholder="e.g. 23 345 4567"
                  className={cn(
                    'flex-1 h-10 px-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1',
                    phoneError ? 'focus:ring-status-error' : 'focus:ring-brand-primary',
                  )}
                />
                {countryPickerOpen ? (
                  <CountryPicker
                    selectedDial={countryCode}
                    search={countrySearch}
                    onSearchChange={setCountrySearch}
                    onSelect={(c) => {
                      setCountryCode(c.dial);
                      setCountryIso2(c.iso2);
                      setCountryPickerOpen(false);
                      setCountrySearch('');
                    }}
                    onClose={() => setCountryPickerOpen(false)}
                  />
                ) : null}
              </div>
              {validatePhone.isPending ? (
                <p className="text-xs text-text-muted mt-1">Validating phone…</p>
              ) : phoneError ? (
                <p className="text-xs text-status-error mt-1">{phoneError}</p>
              ) : null}
            </Field>

            <Field label="Address">
              <SearchInput
                value={address}
                onChange={setAddress}
                placeholder="Enter address"
              />
            </Field>

            <Field label="Apartment">
              <TextInput
                value={apartment}
                onChange={setApartment}
                placeholder="Enter apartment number"
              />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

function Field({ label, required, children }: FieldProps) {
  return (
    <div>
      <label className="block text-xs font-medium text-text-muted mb-2">
        {label} {required ? <span className="text-status-error">*</span> : null}
      </label>
      {children}
    </div>
  );
}

interface InputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
}

function TextInput({ value, onChange, placeholder }: InputProps) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-10 px-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1 focus:ring-brand-primary"
    />
  );
}

function SearchInput({ value, onChange, placeholder }: InputProps) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-3 size-4 text-text-hint pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-10 pl-8 pr-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1 focus:ring-brand-primary"
      />
    </div>
  );
}

/**
 * RelationshipInput — searches the backend master list (`/api/Master/
 * Relation`) and returns the full `Relationship` entry on pick. We need
 * the UUID `id` (not just the display name) for the save endpoint —
 * passing a free-text label trips a backend NRE on the value-set lookup.
 */
interface RelationshipInputProps {
  selected: Relationship | null;
  onChange: (next: Relationship | null) => void;
}

function RelationshipInput({ selected, onChange }: RelationshipInputProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Keep the input value in sync with a picked entry so the label
  // displays after selection. A user typing replaces it again.
  const display = open ? query : (selected?.name ?? query);
  const { data: relationships = [] } = useRelationships({ query });

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-3 size-4 text-text-hint pointer-events-none" />
        <input
          type="text"
          value={display}
          onChange={(e) => {
            setQuery(e.target.value);
            // Typing invalidates the previous pick — caller must
            // re-select to enable Save.
            if (selected) onChange(null);
            setOpen(true);
          }}
          onFocus={() => {
            setQuery(selected?.name ?? '');
            setOpen(true);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          placeholder="Enter relationship"
          className="w-full h-10 pl-8 pr-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1 focus:ring-brand-primary"
        />
      </div>
      {open && relationships.length > 0 ? (
        <div
          className={cn(
            'absolute z-20 left-0 right-0 mt-1 rounded-md border border-border-default bg-surface-card shadow-lg max-h-45 overflow-y-auto scrollbar-hide',
          )}
        >
          {relationships.map((r) => (
            <button
              key={r.id ?? r.name}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(r);
                setQuery(r.name ?? '');
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-grey-100"
            >
              {r.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface CountryPickerProps {
  selectedDial: string;
  search: string;
  onSearchChange: (next: string) => void;
  onSelect: (country: CountryEntry) => void;
  onClose: () => void;
}

/**
 * CountryPicker — dropdown of dial codes shown beneath the country
 * trigger. Filters by ISO2 / dial / country name. Click outside (the
 * backdrop) dismisses without selecting.
 */
function CountryPicker({
  selectedDial,
  search,
  onSearchChange,
  onSelect,
  onClose,
}: CountryPickerProps) {
  const q = search.trim().toLowerCase();
  const visible = q
    ? COUNTRIES.filter(
        (c) =>
          c.iso2.includes(q) ||
          c.dial.toLowerCase().includes(q) ||
          c.name.toLowerCase().includes(q),
      )
    : COUNTRIES;

  return (
    <>
      <button
        type="button"
        aria-label="Close country picker"
        onClick={onClose}
        className="fixed inset-0 z-10 cursor-default"
      />
      <div className="absolute z-20 left-0 top-full mt-1 w-65 rounded-md border border-border-default bg-surface-card shadow-lg overflow-hidden">
        <div className="p-2 border-b border-border-subtle">
          <div className="relative">
            <Search className="absolute left-3 top-3 size-4 text-text-hint pointer-events-none" />
            <input
              autoFocus
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search country"
              className="w-full h-10 pl-8 pr-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
          </div>
        </div>
        <div
          className={cn(
            'max-h-70 overflow-y-auto scrollbar-hide py-1',
          )}
        >
          {visible.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-4">
              No matching countries.
            </p>
          ) : (
            visible.map((c) => (
              <button
                key={`${c.iso2}-${c.dial}`}
                type="button"
                onClick={() => onSelect(c)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-grey-100',
                  c.dial === selectedDial ? 'bg-blue-100' : '',
                )}
              >
                <img
                  src={flagSrc(c.iso2)}
                  alt={c.iso2.toUpperCase()}
                  width={24}
                  height={18}
                  className="rounded-sm flex-shrink-0"
                  loading="lazy"
                />
                <span className="text-sm text-text-primary tabular-nums">
                  {c.dial}
                </span>
                <span className="text-xs text-text-muted ml-auto">
                  ({c.iso2.toUpperCase()})
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </>
  );
}
